import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Check,
  Mic,
  MessageSquare,
  Clock,
  BarChart3,
  FileText,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { HeroGlow } from "@/components/animations/HeroGlow";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { SiteNav } from "@/components/landing/SiteNav";
import { SiteFooter } from "@/components/landing/SiteFooter";
import { MemoReceipt } from "@/components/landing/MemoReceipt";

/* ── Scroll-reveal wrapper ── */
function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-700 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
        className
      )}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      {children}
    </div>
  );
}

/* ── Page ── */
const Home = () => {
  const [heroReady, setHeroReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteNav />

      <main className="flex-1 pt-20">
        {/* ═══════════════════════ HERO ═══════════════════════ */}
        <section className="relative overflow-hidden" aria-label="Hero">
          <HeroGlow />

          <div className="container mx-auto px-4 sm:px-6 pt-16 sm:pt-28 lg:pt-36 pb-16 sm:pb-24 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Signal motif */}
              <div
                className={cn(
                  "flex items-center gap-2 justify-center mb-8 transition-all duration-700",
                  heroReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                )}
              >
                <span className="h-2 w-2 rounded-full bg-success" />
                <span className="h-2 w-2 rounded-full bg-warning" />
                <span className="h-2 w-2 rounded-full bg-danger" />
              </div>

              <h1
                className={cn(
                  "text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tight leading-[0.95]",
                  "transition-all duration-1000 ease-out",
                  heroReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
              >
                Your weekly
                <br />
                <span className="gradient-text">signal.</span>
              </h1>

              <p
                className={cn(
                  "text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto mt-6 leading-relaxed",
                  "transition-all duration-700 ease-out",
                  heroReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                )}
                style={{ transitionDelay: "200ms" }}
              >
                Track 3–9 KPIs weekly. Get board-ready clarity in 20 seconds.
                Delivered on WhatsApp.
              </p>

              {/* CTA pair */}
              <div
                className={cn(
                  "flex flex-col sm:flex-row items-center justify-center gap-4 mt-10",
                  "transition-all duration-700 ease-out",
                  heroReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                )}
                style={{ transitionDelay: "350ms" }}
              >
                <Link to="/auth">
                  <Button
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 py-6 text-base font-semibold shadow-xl hover:shadow-2xl transition-all glow-accent"
                  >
                    Start your first Memo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button
                    variant="ghost"
                    size="lg"
                    className="rounded-full px-6 py-6 text-base text-muted-foreground hover:text-foreground"
                  >
                    See how it works
                  </Button>
                </Link>
              </div>

              {/* Trust badges */}
              <div
                className={cn(
                  "flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-muted-foreground mt-10",
                  "transition-all duration-700 ease-out",
                  heroReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                )}
                style={{ transitionDelay: "500ms" }}
              >
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-success" /> Voice-first
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-success" /> WhatsApp delivery
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-success" /> Free forever plan
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════ PROBLEM: EDITORIAL ASYMMETRIC ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-20 sm:py-32" aria-label="The Problem">
          <Reveal>
            <div className="max-w-5xl mx-auto grid md:grid-cols-[1.2fr_1fr] gap-8 md:gap-16 items-start">
              <div>
                <p className="text-[11px] font-semibold text-danger uppercase tracking-[0.25em] mb-4">
                  The problem
                </p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                  Kill the Sunday
                  <br />
                  night scramble.
                </h2>
              </div>
              <div className="md:pt-14">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Every week, founders waste hours hunting for metrics across
                  spreadsheets, dashboards, and Slack threads. Board calls arrive
                  with no clarity. Memo replaces the chaos with one clean signal.
                </p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ THE ARTIFACT: WOW MOMENT 1 ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-28" aria-label="Weekly Memo">
          <Reveal>
            <p className="text-[11px] font-semibold text-primary text-center uppercase tracking-[0.25em] mb-3">
              Your weekly memo
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center mb-14">
              One artifact. Total clarity.
            </h2>
          </Reveal>
          <MemoReceipt />
        </section>

        {/* ═══════════════════════ VOICE-FIRST ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-20 sm:py-32" aria-label="Voice Input">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-accent/10 flex items-center justify-center mb-8 animate-pulse-glow">
                <Mic className="h-10 w-10 text-accent" />
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Say it.
                <br />
                <span className="text-muted-foreground font-bold">We'll track it.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-lg mx-auto mt-6 leading-relaxed">
                "MRR is 15 lakhs, NPS is 42, burn rate 8 lakhs." One sentence.
                All submitted. Memo's AI extracts your KPIs instantly.
              </p>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ WHATSAPP DELIVERY: WOW MOMENT 2 ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-28" aria-label="WhatsApp Delivery">
          <Reveal>
            <p className="text-[11px] font-semibold text-success text-center uppercase tracking-[0.25em] mb-3">
              Delivered to your pocket
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center mb-14">
              30-second board update. On WhatsApp.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="max-w-sm mx-auto">
              <div className="bg-card rounded-3xl border border-border/50 p-6 shadow-2xl hover:shadow-glow transition-shadow duration-500">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-success/20 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Memo</p>
                    <p className="text-[11px] text-muted-foreground">Sunday 11:00 AM</p>
                  </div>
                </div>
                <div className="bg-muted/50 rounded-2xl p-4 space-y-2.5">
                  <p className="font-semibold text-sm">🎯 Waypoint — Week 15</p>
                  <div className="space-y-1.5 text-muted-foreground text-xs">
                    <p className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-success" />
                      New MRR ₹9.7L ↑12%
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-warning" />
                      Active Subs 2,870 →
                    </p>
                    <p className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-danger" />
                      NPS 42 ↓15%
                    </p>
                  </div>
                  <p className="text-[11px] border-t border-border/50 pt-2.5 mt-2.5">
                    <span className="font-medium text-foreground">Focus:</span>{" "}
                    NPS dropped — schedule user interviews
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ SPEED ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-20 sm:py-32" aria-label="Speed">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-8">
                <Clock className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
                Submit in <span className="text-accent tabular-nums">20s</span>.
              </h2>
              <p className="text-lg text-muted-foreground max-w-lg mx-auto mt-6 leading-relaxed">
                Voice or keyboard. Individual or bulk. Memo collects, flags
                outliers, and generates your summary — before your coffee gets cold.
              </p>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ PORTFOLIO ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-20 sm:py-28" aria-label="Portfolio View">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-8">
                <BarChart3 className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
                One view. All companies.
              </h2>
              <p className="text-lg text-muted-foreground max-w-lg mx-auto mt-6 leading-relaxed">
                Fund managers and multi-company founders get a portfolio
                dashboard with traffic-light signals across every entity, every week.
              </p>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ EXPORTS ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-20 sm:py-28" aria-label="Exports">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <div className="mx-auto h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-8">
                <FileText className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
                Board-ready exports.
              </h2>
              <p className="text-lg text-muted-foreground max-w-lg mx-auto mt-6 leading-relaxed">
                Weekly PDFs, monthly rollups, CSV exports. Formatted for board
                meetings and LP updates. Zero manual work.
              </p>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ BUILT FOR INDIA ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-28" aria-label="Localization">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-center mb-10">
              Built for Indian founders.
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {[
                { icon: "₹", label: "INR native" },
                { icon: "🕐", label: "IST timezone" },
                { icon: null, label: "Team roles", LucideIcon: Users },
                { icon: "🤖", label: "AI summaries" },
              ].map((item, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-card border border-border/50 text-sm font-medium shadow-xs hover:shadow-md transition-all hover:-translate-y-0.5 cursor-default"
                >
                  {item.LucideIcon ? (
                    <item.LucideIcon className="h-4 w-4" />
                  ) : (
                    <span>{item.icon}</span>
                  )}
                  {item.label}
                </span>
              ))}
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ HOW IT WORKS: SIGNAL TIMELINE ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-28" aria-label="How it works">
          <Reveal>
            <div className="bg-card/50 border border-border/40 rounded-3xl p-8 sm:p-14 max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">
                Simple weekly cadence
              </h2>
              <div className="space-y-4 relative">
                {/* Vertical signal line */}
                <div className="absolute left-[13px] top-5 bottom-5 w-px bg-border/50 hidden sm:block" />

                {[
                  "Define 3–9 KPIs with owners and target bands",
                  "Get reminders 48h, 12h, 2h before deadline",
                  "Submit values via voice, chat, or web",
                  "Everyone receives a concise summary with traffic lights",
                ].map((step, i) => (
                  <Reveal key={i} delay={i * 100}>
                    <div className="flex items-start gap-4 sm:gap-5 bg-background/60 p-4 sm:p-5 rounded-xl border border-border/30 shadow-xs">
                      <div className="h-7 w-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-accent relative z-10">
                        {i + 1}
                      </div>
                      <p className="text-foreground text-sm sm:text-base leading-relaxed">
                        {step}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </section>

        {/* ═══════════════════════ FINAL CTA ═══════════════════════ */}
        <section className="container mx-auto px-4 sm:px-6 py-20 sm:py-32 text-center" aria-label="Get Started">
          <Reveal>
            <div className="max-w-xl mx-auto space-y-6">
              {/* Signal motif */}
              <div className="flex items-center gap-2 justify-center mb-6">
                <span className="h-2 w-2 rounded-full bg-success" />
                <span className="h-2 w-2 rounded-full bg-warning" />
                <span className="h-2 w-2 rounded-full bg-danger" />
              </div>

              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Stop scrambling.
                <br />
                <span className="gradient-text">Start submitting.</span>
              </h2>

              <p className="text-lg text-muted-foreground">
                Join founders who track what matters and communicate with
                clarity — every week.
              </p>

              <Link to="/auth">
                <Button
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 py-6 text-base font-semibold shadow-xl hover:shadow-2xl transition-all glow-accent mt-4"
                >
                  Start your first Memo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Home;
